import React, { useEffect, useState } from "react";
import { Edit, Save, Trash2, X, Plus, RotateCw } from "lucide-react";
import AdminCardActions from "../components/AdminCardActions";
import ToolbarSearch from "../components/ToolbarSearch";
import {
    fetchConfigValues,
    updateConfigValue,
    deleteConfigValue,
    addConfigValue,
} from "../services/configs";

const ConfigValuesAdmins = () => {
    const [configValues, setConfigValues] = useState([]);
    const [filteredConfigValues, setFilteredConfigValues] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("table_name");
    const [sortDir, setSortDir] = useState("asc");
    const [editingConfigValue, setEditingConfigValue] = useState(null);
    const [editedData, setEditedData] = useState({
        table_name: "",
        column_name: "",
        value_name: "",
    });
    const [newConfigValue, setNewConfigValue] = useState({
        table_name: "",
        column_name: "",
        value_name: "",
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadConfigValues();

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = configValues.filter((item) =>
            (item.table_name || "").toString().toLowerCase().includes(q) ||
            (item.column_name || "").toString().toLowerCase().includes(q) ||
            (item.value_name || "").toString().toLowerCase().includes(q)
        );
        setFilteredConfigValues(filtered);
    }, [configValues, searchQuery]);

    const loadConfigValues = async () => {
        setLoading(true);
        const data = await fetchConfigValues();
        setConfigValues(data);
        setLoading(false);
    };

    const handleEdit = (configValue) => {
        setEditingConfigValue(configValue.id);
        setEditedData({ ...configValue });
    };

    const handleCancelEdit = () => {
        setEditingConfigValue(null);
        setEditedData({ table_name: "", column_name: "", value_name: "" });
    };

    const handleSave = async (id) => {
        await updateConfigValue(id, editedData);
        setEditingConfigValue(null);
        loadConfigValues();
    };

    const handleDelete = async (id) => {
        if (window.confirm("Видалити цей запис?")) {
            await deleteConfigValue(id);
            loadConfigValues();
        }
    };

    const handleAddConfigValue = async () => {
        if (
            !newConfigValue.table_name.trim() ||
            !newConfigValue.column_name.trim() ||
            !newConfigValue.value_name.trim()
        ) {
            alert("Всі поля повинні бути заповнені!");
            return;
        }
        await addConfigValue(newConfigValue);
        setNewConfigValue({ table_name: "", column_name: "", value_name: "" });
        setShowAddModal(false);
        loadConfigValues();
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredConfigValues) return [];
        const sorted = [...filteredConfigValues];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            if (typeof valA === "string") {
                valA = valA.toLowerCase();
                valB = valB.toLowerCase();
            }
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowAddModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadConfigValues} disabled={loading} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук за таблицею, колонкою або значенням..."
                    />
                </div>
            </div>

            {showAddModal && (
                <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Додати значення</h3>
                        <input
                            type="text"
                            placeholder="Назва таблиці"
                            value={newConfigValue.table_name}
                            onChange={(e) =>
                                setNewConfigValue({ ...newConfigValue, table_name: e.target.value })
                            }
                        />
                        <input
                            type="text"
                            placeholder="Назва колонки"
                            value={newConfigValue.column_name}
                            onChange={(e) =>
                                setNewConfigValue({ ...newConfigValue, column_name: e.target.value })
                            }
                        />
                        <input
                            type="text"
                            placeholder="Значення"
                            value={newConfigValue.value_name}
                            onChange={(e) =>
                                setNewConfigValue({ ...newConfigValue, value_name: e.target.value })
                            }
                        />
                        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                            <button className="add-btn" onClick={handleAddConfigValue}>Додати</button>
                            <button onClick={() => setShowAddModal(false)} className="add-btn cancel-btn">
                                Скасувати
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile ? (
                <div className="config-grid">
                    {filteredConfigValues.map((configValue) => (
                        <div key={configValue.id} className="config-card">
                            <div>
                                <strong>Таблиця:</strong>{" "}
                                {editingConfigValue === configValue.id ? (
                                    <input
                                        value={editedData.table_name}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, table_name: e.target.value })
                                        }
                                    />
                                ) : (
                                    configValue.table_name
                                )}
                            </div>

                            <div>
                                <strong>Колонка:</strong>{" "}
                                {editingConfigValue === configValue.id ? (
                                    <input
                                        value={editedData.column_name}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, column_name: e.target.value })
                                        }
                                    />
                                ) : (
                                    configValue.column_name
                                )}
                            </div>

                            <div>
                                <strong>Значення:</strong>{" "}
                                {editingConfigValue === configValue.id ? (
                                    <input
                                        value={editedData.value_name}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, value_name: e.target.value })
                                        }
                                    />
                                ) : (
                                    configValue.value_name
                                )}
                            </div>

                            <AdminCardActions
                                editing={editingConfigValue === configValue.id}
                                onEdit={() => handleEdit(configValue)}
                                onSave={() => handleSave(configValue.id)}
                                onCancel={handleCancelEdit}
                                onDelete={() => handleDelete(configValue.id)}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="table-responsive">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("table_name")}>
                                    Таблиця {sortBy === "table_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("column_name")}>
                                    Колонка {sortBy === "column_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("value_name")}>
                                    Значення {sortBy === "value_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((configValue) => (
                                <tr key={configValue.id}>
                                    <td>
                                        {editingConfigValue === configValue.id ? (
                                            <input
                                                value={editedData.table_name}
                                                onChange={(e) =>
                                                    setEditedData({ ...editedData, table_name: e.target.value })
                                                }
                                            />
                                        ) : (
                                            configValue.table_name
                                        )}
                                    </td>
                                    <td>
                                        {editingConfigValue === configValue.id ? (
                                            <input
                                                value={editedData.column_name}
                                                onChange={(e) =>
                                                    setEditedData({ ...editedData, column_name: e.target.value })
                                                }
                                            />
                                        ) : (
                                            configValue.column_name
                                        )}
                                    </td>
                                    <td>
                                        {editingConfigValue === configValue.id ? (
                                            <input
                                                value={editedData.value_name}
                                                onChange={(e) =>
                                                    setEditedData({ ...editedData, value_name: e.target.value })
                                                }
                                            />
                                        ) : (
                                            configValue.value_name
                                        )}
                                    </td>
                                    <td className="actions">
                                        {editingConfigValue === configValue.id ? (
                                            <>
                                                <button
                                                    className="icon-button"
                                                    onClick={() => handleSave(configValue.id)}
                                                    title="Зберегти"
                                                >
                                                    <Save className="icon-btn" />
                                                </button>
                                                <button
                                                    className="icon-button"
                                                    onClick={handleCancelEdit}
                                                    title="Скасувати"
                                                >
                                                    <X className="icon-btn" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className="icon-button"
                                                    onClick={() => handleEdit(configValue)}
                                                    title="Редагувати"
                                                >
                                                    <Edit className="icon-btn" />
                                                </button>
                                                <button
                                                    className="icon-button"
                                                    onClick={() => handleDelete(configValue.id)}
                                                    title="Видалити"
                                                >
                                                    <Trash2 className="icon-btn delete" />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ConfigValuesAdmins;
